/**
 * appt-setup.tsx — the Setup rail for an APPOINTMENT journey (2026-08-31).
 *
 * Its own module rather than seven more panels inside NewJourneys.tsx, which is
 * already 4,300 lines. The host passes the document, a patcher and the live
 * libraries; everything else is here.
 *
 * THE SHAPE OF THIS SURFACE IS DECIDED BY ONE FACT: an appointment journey has
 * no record (`APPOINTMENT_BINDING.ownsRecord: false`). It is synthesised, one
 * document per line of business, from whichever catalogue services are bookable.
 * So Setup splits three ways and every control says which it is:
 *
 *   A · journey-owned  → apptSetup on the document, written by the host's patch
 *   B · referenced     → catalogue truth, shown read-only with a jump. Editing
 *                        it here would change every queue and funnel that reads
 *                        the same service row.
 *   C · blocked        → needs a field that does not exist yet. Rendered as a
 *                        disabled row that says what it will do, never as a
 *                        control that silently does nothing.
 *
 * Service-scoped settings (questions, and — once the API lands — surveys,
 * promos and flows) appear here as a per-service MATRIX and never on a page,
 * because at design time nobody knows which service the customer will pick.
 */
import { useMemo } from "react";
import { CatalogPicker, resolveSelection, type PickerItem, type PickerGroup, type PickerSel } from "./catalog-picker";
import { Btn, Icon, T } from "@v0y4ge/jrni-ui";
import type { ApptSetupLike, Doc } from "./nj-model";

/* ── the shapes the host hands in ─────────────────────────────────────── */
export interface ApptService {
  id: string; name: string; category?: string; lob?: string;
  durationMin?: number | null; preMin?: number; postMin?: number;
  channels?: { inPerson: boolean; phone: boolean; video: boolean };
  questionGroupId?: string | null;
  minAdvance?: string | null; maxAdvance?: string | null; minCancel?: string | null;
  disableBefore?: string | null; disableAfter?: string | null;
}
export interface ApptLibs {
  services: ApptService[];
  categories: { id: string; name: string }[];
  locations: { id: string; name: string; address?: string }[];
  questionGroups: { id: string; name: string; count: number }[];
  surveys: { id: string; name: string }[];
  promos: { id: string; label: string }[];
  flows: { id: string; name: string }[];
  /** Real availability for the funnel's slug, or null when it has no slug yet. */
  availability?: { days: number; slots: number } | null;
}
export type ApptView = "aservices" | "aplaces" | "awindow" | "aperservice" | "adetails" | "amessaging" | "publish";

export const APPT_SETUP_LABEL: Record<string, string> = {
  aservices: "Services",
  aplaces: "Places & methods",
  awindow: "Booking window",
  aperservice: "Per-service settings",
  adetails: "Details & consent",
  amessaging: "Messaging",
};

export const APPT_SETUP_DEFAULTS: ApptSetupLike = {
  serviceSelection: [], groupByCategory: false, locationIds: [],
  methods: { inPerson: true, phone: false, video: false },
  standardFields: ["email"], consent: {},
};

/** Readiness, declared ONCE and read by the tab dot, the in-panel band and the
 *  publish gate — so the three cannot disagree, same rule as the other types. */
export function apptReadiness(a: Partial<ApptSetupLike>, doc: Doc): { tab: ApptView; label: string; done: boolean }[] {
  const m = a.methods ?? APPT_SETUP_DEFAULTS.methods;
  const remote = !!m.phone || !!m.video;
  return [
    { tab: "aservices", label: "At least one service offered", done: (a.serviceSelection ?? []).length > 0 },
    { tab: "aplaces", label: "A place, or a method that needs none", done: (a.locationIds ?? []).length > 0 || remote },
    { tab: "adetails", label: "Email captured on the About you page", done: (a.standardFields ?? []).indexOf("email") > -1 },
    { tab: "publish", label: "A publish slug", done: !!doc.publish?.slug },
  ];
}

/* ── local style vocabulary, matching the ported Setup chrome ─────────── */
const FONT = '"DM Sans", "Helvetica Neue", sans-serif';
const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#333333", display: "block", marginBottom: 5 };
const input: React.CSSProperties = { width: "100%", height: 34, padding: "0 10px", border: "1px solid #D1D4DC", borderRadius: 8, fontSize: 12.5, fontFamily: FONT, boxSizing: "border-box", background: "#FFFFFF" };
const card: React.CSSProperties = { maxWidth: 620, display: "flex", flexDirection: "column", gap: 16 };
const sec: React.CSSProperties = { fontSize: 10.5, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "#697180", marginBottom: 8 };
const note: React.CSSProperties = { fontSize: 11.5, color: "#697180", lineHeight: 1.6 };
const mono: React.CSSProperties = { fontFamily: "ui-monospace, Menlo, monospace" };

/** The class badge every control carries, so an operator always knows whether a
 *  value is theirs, the catalogue's, or not yet possible. */
function Owned({ cls }: { cls: "A" | "B" | "C" }) {
  const m = {
    A: { t: "This funnel", bg: "#EEF6EF", fg: "#1E5C3F" },
    B: { t: "Catalogue · read-only", bg: "#F1EFFC", fg: "#5142C7" },
    C: { t: "Needs the API", bg: "#FFF7E8", fg: "#8A5A08" },
  }[cls];
  return <span style={{ fontSize: 9.5, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: m.bg, color: m.fg, whiteSpace: "nowrap" }}>{m.t}</span>;
}

function Row({ title, cls, children, sub }: { title: string; cls: "A" | "B" | "C"; children?: React.ReactNode; sub?: string }) {
  return (
    <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, padding: "12px 14px", background: "#FFFFFF" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: sub || children ? 7 : 0, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, flex: 1, minWidth: 0 }}>{title}</span>
        <Owned cls={cls} />
      </div>
      {sub ? <div style={{ ...note, marginBottom: children ? 8 : 0 }}>{sub}</div> : null}
      {children}
    </div>
  );
}

export interface ApptSetupProps {
  view: ApptView;
  doc: Doc;
  setup: Partial<ApptSetupLike>;
  libs: ApptLibs;
  onPatch: (patch: Partial<ApptSetupLike>) => void;
  onPublish: (patch: Record<string, string>) => void;
  goRoute: (route: string, params?: Record<string, string>) => void;
}

export function ApptSetupPanel({ view, doc, setup, libs, onPatch, onPublish, goRoute }: ApptSetupProps) {
  const a = { ...APPT_SETUP_DEFAULTS, ...setup };
  const chosen = a.serviceSelection ?? [];
  /** Picker items and groups: services grouped by their line of business, which
   *  is what the operator asked every picker in the designer to show. */
  const svcItems: PickerItem[] = libs.services.map((s) => {
    const ch = s.channels ?? { inPerson: true, phone: false, video: false };
    const methods = [ch.inPerson ? "In person" : null, ch.phone ? "Phone" : null, ch.video ? "Video" : null].filter(Boolean).join(" · ");
    return {
      id: s.id, name: s.name,
      group: s.lob ?? s.category ?? null,
      meta: [s.durationMin ? `${s.durationMin} min` : "no duration set", methods || null],
      keywords: (s as any).description ?? "",
    };
  });
  const svcGroups: PickerGroup[] = (libs.categories ?? []).map((c, i) => ({ id: c.id, name: c.name, hue: ["#0AA396", "#2D60EB", "#D61A55", "#916D0F", "#7C3AED"][i % 5]! }));
  /* A whole-department rule is stored as a `dept:` id inside the SAME string[]
     the funnel already persisted, so no stored shape changes and an older
     funnel round-trips untouched. */
  const DEPT = "dept:";
  const pickerValue: PickerSel[] = chosen.map((id) => (id.startsWith(DEPT) ? { type: "group", id: id.slice(DEPT.length) } : { type: "item", id }) as PickerSel);
  const commitPicker = (next: PickerSel[]) =>
    onPatch({ serviceSelection: next.map((s) => (s.type === "group" ? DEPT + s.id : s.id)) });
  const offered = useMemo(
    () => resolveSelection(pickerValue, svcItems)
      .map((it) => libs.services.filter((s) => s.id === it.id)[0])
      .filter(Boolean) as ApptService[],
    [chosen.join("|"), libs.services],
  );

  /* toggleService is gone with the flat checkbox list — CatalogPicker owns the
     add/remove/reorder and commitPicker is the only writer. */

  /* ── 1 · Services ─────────────────────────────────────────────────── */
  if (view === "aservices") {
    return (
      <div style={card}>
        <div>
          <div style={sec}>Services this funnel offers</div>
          <div style={note}>Add whole departments or single services — the order here is the order on the Book a service page. Duration and delivery methods come from the catalogue and are shown so you can see what you are offering; they are not editable from inside one funnel.</div>
        </div>
        {/* The one picker (2026-09-02, operator): the same component the queue,
            One Stop and ticket pickers use. This was a flat checkbox list whose
            order was click order shown as a number badge — no departments, no
            search, no way to take a whole line of business. */}
        <CatalogPicker
          items={svcItems}
          groups={svcGroups}
          value={pickerValue}
          onChange={commitPicker}
          sourceLabel="Your catalogue"
          selectedLabel="This funnel offers"
          noun="service"
          searchPlaceholder="Search services or departments…"
          emptySource="No bookable services in this line of business yet."
          emptySelection="Pick what this funnel books — they appear here in the order the customer sees." />
        {libs.services.length === 0 ? (
          <div style={note}>
            <a href="#" onClick={(e) => { e.preventDefault(); goRoute("ops.catalogue"); }}>Open the catalogue</a> to mark some services bookable.
          </div>
        ) : null}
        <Row title="Group services by category on the page" cls="A">
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, cursor: "pointer" }}>
            <input type="checkbox" checked={!!a.groupByCategory} onChange={(e) => onPatch({ groupByCategory: e.target.checked })} />
            Show category headings
          </label>
        </Row>
        <Row title="Duration, buffers and price" cls="B"
          sub="One catalogue row per service, shared with queueing and events. Changing a duration here would change it everywhere.">
          <Btn size="sm" variant="secondary" onClick={() => goRoute("ops.catalogue")}>Open the catalogue</Btn>
        </Row>
      </div>
    );
  }

  /* ── 2 · Places & methods ─────────────────────────────────────────── */
  if (view === "aplaces") {
    const ceiling = offered.reduce(
      (acc, s) => {
        const ch = s.channels ?? { inPerson: true, phone: false, video: false };
        return { inPerson: acc.inPerson || ch.inPerson, phone: acc.phone || ch.phone, video: acc.video || ch.video };
      },
      { inPerson: false, phone: false, video: false },
    );
    const methodRow = (k: "inPerson" | "phone" | "video", name: string) => {
      const allowed = offered.length === 0 || ceiling[k];
      const on = !!a.methods[k];
      return (
        <label key={k} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, padding: "8px 10px", borderRadius: 8, border: "1px solid #E6E8EC", opacity: allowed ? 1 : 0.5, cursor: allowed ? "pointer" : "not-allowed" }}>
          <input type="checkbox" checked={on && allowed} disabled={!allowed} onChange={(e) => onPatch({ methods: { ...a.methods, [k]: e.target.checked } })} />
          <span style={{ flex: 1 }}>{name}</span>
          {!allowed ? <span style={{ fontSize: 10.5, color: "#8A5A08" }}>no offered service supports it</span> : null}
        </label>
      );
    };
    return (
      <div style={card}>
        <div>
          <div style={sec}>Where and how</div>
          <div style={note}>A funnel cannot offer a method none of its services supports — those rows are disabled rather than selectable and silently dropped at booking time.</div>
        </div>
        <Row title="Delivery methods" cls="A">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {methodRow("inPerson", "In person")}
            {methodRow("phone", "Phone")}
            {methodRow("video", "Video")}
          </div>
        </Row>
        <Row title="Locations offered" cls="A" sub="Leave all unticked to offer every location these services are available at.">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {libs.locations.length === 0 ? <span style={note}>No locations in the library yet.</span> : libs.locations.map((l) => {
              const on = (a.locationIds ?? []).indexOf(l.id) > -1;
              return (
                <button key={l.id} onClick={() => onPatch({ locationIds: on ? (a.locationIds ?? []).filter((x) => x !== l.id) : (a.locationIds ?? []).concat(l.id) })}
                  style={{ padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 11.5, fontWeight: 700, border: `1.5px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#5142C7" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563" }}>
                  {l.name}
                </button>
              );
            })}
          </div>
        </Row>
        <Row title="Opening hours" cls="B" sub="A location's hours bound every slot at it. Edited in the locations library.">
          <Btn size="sm" variant="secondary" onClick={() => goRoute("locations")}>Open locations</Btn>
        </Row>
      </div>
    );
  }

  /* ── 3 · Booking window ───────────────────────────────────────────── */
  if (view === "awindow") {
    return (
      <div style={card}>
        <div>
          <div style={sec}>The rules around the times</div>
          <div style={note}>Nothing here sets a time. Slots are computed from advisor working patterns, service duration and buffers, and location hours — what Setup owns is how far ahead booking opens and how late it closes, and today those are catalogue fields per service.</div>
        </div>
        {offered.length === 0 ? (
          <div style={{ border: "1px dashed #D1D4DC", borderRadius: 9, padding: "18px 12px", textAlign: "center", ...note }}>Pick services first — the windows are per service.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {offered.map((s) => (
              <div key={s.id} style={{ border: "1px solid #E6E8EC", borderRadius: 9, padding: "10px 12px", background: "#FFFFFF" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, flex: 1, minWidth: 0 }}>{s.name}</span>
                  <Owned cls="B" />
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 14px", fontSize: 11, color: "#697180" }}>
                  <span>Opens {s.minAdvance ?? "—"} ahead</span>
                  <span>Closes {s.maxAdvance ?? "—"} out</span>
                  <span>Cancel by {s.minCancel ?? "—"}</span>
                  {s.disableBefore ? <span>from {s.disableBefore}</span> : null}
                  {s.disableAfter ? <span>until {s.disableAfter}</span> : null}
                </div>
                <a href="#" onClick={(e) => { e.preventDefault(); goRoute("ops.catalogue", { service: s.id }); }} style={{ fontSize: 11, fontWeight: 700, display: "inline-block", marginTop: 6 }}>Edit in the catalogue →</a>
              </div>
            ))}
          </div>
        )}
        <Row title="A window for this funnel only" cls="C"
          sub="A funnel-specific notice period — shorter for walk-in-adjacent booking, longer for private advice — needs a per-funnel override the catalogue cannot express. Requested; until it lands the catalogue value applies everywhere.">
          <span style={{ ...note, ...mono, fontSize: 10.5 }}>CD-API-REQUEST-journey-availability-2026-08-14</span>
        </Row>
        <Row title="Availability preview" cls="B"
          sub={doc.publish?.slug
            ? (libs.availability ? `${libs.availability.days} days with ${libs.availability.slots} bookable slots in the next four weeks.` : "No slots returned for this funnel yet — usually no advisor offers these services.")
            : "Publish a slug first — availability is read per funnel."} />
      </div>
    );
  }

  /* ── 4 · Per-service settings ─────────────────────────────────────── */
  if (view === "aperservice") {
    const gName = (id?: string | null) => (id ? libs.questionGroups.filter((g) => g.id === id)[0] : null);
    return (
      <div style={{ ...card, maxWidth: 760 }}>
        <div>
          <div style={sec}>What each service asks and sends</div>
          <div style={note}>This is a matrix rather than a picker, and it is the reason these settings left the page designer: at design time nobody knows which service the customer will pick, so a single journey-wide value would be a confident answer to an unanswerable question. Questions are live today; surveys, promos and flows are requested.</div>
        </div>
        {offered.length === 0 ? (
          <div style={{ border: "1px dashed #D1D4DC", borderRadius: 9, padding: "18px 12px", textAlign: "center", ...note }}>Pick services first.</div>
        ) : (
          <div style={{ border: "1px solid #E6E8EC", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1.3fr 1fr", gap: 10, padding: "8px 12px", background: "#F7F7FA", fontSize: 10, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#697180" }}>
              <span>Service</span><span>Booking questions</span><span>Survey · promo · flow</span>
            </div>
            {offered.map((s) => {
              const g = gName(s.questionGroupId);
              return (
                <div key={s.id} style={{ display: "grid", gridTemplateColumns: "1.3fr 1.3fr 1fr", gap: 10, padding: "10px 12px", borderTop: "1px solid #EEF0F3", alignItems: "baseline", fontSize: 11.5 }}>
                  <span style={{ fontWeight: 700 }}>{s.name}</span>
                  <span>
                    {g
                      ? <>{g.name} <span style={{ color: "#697180" }}>· {g.count} question{g.count === 1 ? "" : "s"}</span><br /><a href="#" onClick={(e) => { e.preventDefault(); goRoute("questions.group.edit", { id: g.id }); }} style={{ fontSize: 11 }}>Edit group →</a></>
                      : <span style={{ color: "#8A5A08" }}>None set — <a href="#" onClick={(e) => { e.preventDefault(); goRoute("ops.catalogue", { service: s.id }); }}>set one on the service</a></span>}
                  </span>
                  <span style={{ color: "#8A92A0", fontSize: 10.5, lineHeight: 1.5 }}>Pending the catalogue fields</span>
                </div>
              );
            })}
          </div>
        )}
        {/* Legacy rows — only where a value exists, and gone for good once
            removed (operator ruling). Withdrawing the picker must not silently
            leave a live setting with nowhere to see it. */}
        {a.legacySurveyId || a.legacyPromoId ? (
          <div style={{ border: "1px solid #F0DCB8", background: "#FFFBF2", borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#8A5A08", marginBottom: 6 }}>Attached before per-service settings</div>
            <div style={{ ...note, marginBottom: 9 }}>Journey-wide attachments are no longer offered, but these are still live on this funnel. They keep working until you remove them; removing is permanent.</div>
            {a.legacySurveyId ? (
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 6 }}>
                <span style={{ flex: 1, fontSize: 12 }}>Survey · {libs.surveys.filter((x) => x.id === a.legacySurveyId)[0]?.name ?? a.legacySurveyId}</span>
                <Btn size="sm" variant="secondary" danger onClick={() => onPatch({ legacySurveyId: null })}>Remove</Btn>
              </div>
            ) : null}
            {a.legacyPromoId ? (
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <span style={{ flex: 1, fontSize: 12 }}>Promo · {libs.promos.filter((x) => x.id === a.legacyPromoId)[0]?.label ?? a.legacyPromoId}</span>
                <Btn size="sm" variant="secondary" danger onClick={() => onPatch({ legacyPromoId: null })}>Remove</Btn>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  /* ── 5 · Details & consent ────────────────────────────────────────── */
  if (view === "adetails") {
    const fields = a.standardFields ?? ["email"];
    const toggleField = (k: string) => onPatch({ standardFields: fields.indexOf(k) > -1 ? fields.filter((x) => x !== k) : fields.concat(k) });
    return (
      <div style={card}>
        <div>
          <div style={sec}>The About you page</div>
          <div style={note}>What the funnel asks beyond the service's own question group, and what the customer agrees to. Events keep these on the event record; an appointment funnel has no record, so they live on the journey.</div>
        </div>
        <Row title="Standard fields" cls="A" sub="Name is always captured. Email is required to send a confirmation.">
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {[["email", "Email address"], ["phone", "Mobile number"]].map(([k, name]) => (
              <label key={k} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, cursor: "pointer" }}>
                <input type="checkbox" checked={fields.indexOf(k!) > -1} onChange={() => toggleField(k!)} />
                {name}
              </label>
            ))}
          </div>
        </Row>
        <Row title="Consent" cls="A">
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, cursor: "pointer" }}>
              <input type="checkbox" checked={!!a.consent?.marketing} onChange={(e) => onPatch({ consent: { ...a.consent, marketing: e.target.checked } })} />
              Offer marketing opt-in
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, cursor: "pointer" }}>
              <input type="checkbox" checked={!!a.consent?.contactSharing} onChange={(e) => onPatch({ consent: { ...a.consent, contactSharing: e.target.checked } })} />
              Offer contact-sharing opt-in
            </label>
            <span style={label}>Privacy policy URL</span>
            <input value={a.policyUrl ?? ""} onChange={(e) => onPatch({ policyUrl: e.target.value })} placeholder="https://" style={input} />
          </div>
        </Row>
        <Row title="Terms shown at booking" cls="A">
          <textarea value={a.termsCopy ?? ""} onChange={(e) => onPatch({ termsCopy: e.target.value })} rows={3}
            style={{ ...input, height: "auto", padding: 9, resize: "vertical", lineHeight: 1.5 }} />
        </Row>
        <Row title="After booking, send them to" cls="A" sub="Leave empty to stay on the confirmation page.">
          <input value={a.redirectUrl ?? ""} onChange={(e) => onPatch({ redirectUrl: e.target.value })} placeholder="https://" style={input} />
        </Row>
      </div>
    );
  }

  /* ── 6 · Messaging ────────────────────────────────────────────────── */
  if (view === "amessaging") {
    return (
      <div style={card}>
        <div>
          <div style={sec}>Fallback message flow</div>
          <div style={note}>Once services carry their own flows, this is what runs for a service that names none — and for the moments with no service to resolve from at all. It is a fallback by design, not a limitation.</div>
        </div>
        <Row title="Flow for this funnel" cls="A">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {libs.flows.length === 0 ? <span style={note}>No flows in the library yet.</span> : libs.flows.map((f) => {
              const on = a.messagingFlowId === f.id;
              return (
                <div key={f.id} onClick={() => onPatch({ messagingFlowId: on ? undefined : f.id })}
                  style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 11px", borderRadius: 9, cursor: "pointer", background: on ? "#F6F2FE" : "#FFFFFF", border: `1.5px solid ${on ? "#5142C7" : "#E6E8EC"}` }}>
                  <span style={{ flex: 1, fontSize: 12.5, fontWeight: on ? 700 : 500 }}>{f.name}</span>
                  {on ? <Icon.check size={13} /> : null}
                </div>
              );
            })}
          </div>
        </Row>
        <Row title="A flow per service" cls="C"
          sub="Selected from the flows that already exist in Messaging — a dropdown per service, not a flow to build per service. Requested alongside per-service surveys and promos.">
          <span style={{ ...note, ...mono, fontSize: 10.5 }}>CD-API-REQUEST-service-scoped-attachments-2026-08-31</span>
        </Row>
      </div>
    );
  }

  /* ── 7 · Publish ──────────────────────────────────────────────────── */
  const pub = doc.publish ?? {};
  return (
    <div style={card}>
      <div>
        <div style={sec}>Where this funnel lives</div>
        <div style={note}>An appointment funnel has no record to switch live — publishing means its slug serves. Everything else on this page is how it presents when shared.</div>
      </div>
      <Row title="Slug" cls="A" sub="The path customers book at.">
        <input value={pub.slug ?? ""} onChange={(e) => onPublish({ slug: e.target.value })} placeholder="book-with-us" style={input} />
      </Row>
      <Row title="Host" cls="A">
        <input value={pub.tenantHost ?? ""} onChange={(e) => onPublish({ tenantHost: e.target.value })} placeholder="booking.example.com" style={input} />
      </Row>
      <Row title="SEO title" cls="A">
        <input value={pub.seoTitle ?? ""} onChange={(e) => onPublish({ seoTitle: e.target.value })} style={input} />
      </Row>
      <Row title="Share image" cls="A" sub="Shown when the link is pasted into a chat or a post.">
        <input value={pub.shareImage ?? ""} onChange={(e) => onPublish({ shareImage: e.target.value })} placeholder="https://" style={input} />
      </Row>
    </div>
  );
}
